import React, {Component} from "react"
import ReactDOM from "react-dom"
import styles from "./SignaturePopup.module.css"
import {Gate} from "lib/gate/Gate"
import SignatureCanvas from "react-signature-canvas"
// @ts-ignore
import SignaturePad from "signature_pad"
import {action, computed, observable} from "mobx"
import {observer} from "mobx-react"
import {Model} from "../../Model"

// --- Constants & Configuration ---

const BLANK_SIGNATURE_DATA_URL =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwAAACWCAYAAABkW7XSAAAEYklEQVR4Xu3UAQkAAAwCwdm/9HI83BLIOdw5AgQIRAQWySkmAQIEzmB5AgIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlAABg+UHCBDICBisTFWCEiBgsPwAAQIZAYOVqUpQAgQMlh8gQCAjYLAyVQlKgIDB8gMECGQEDFamKkEJEDBYfoAAgYyAwcpUJSgBAgbLDxAgkBEwWJmqBCVAwGD5AQIEMgIGK1OVoAQIGCw/QIBARsBgZaoSlACBB1YxAJfjJb2jAAAAAElFTkSuQmCC"

const CANVAS_CONFIG = {
    width: 280,
    height: 145,
    fontFamily: '"Dancing Script", cursive',
    initialsFontSize: 84,
    signatureFontSize: 56,
    minFontSize: 14,
    paddingX: 20
}

export type SignatureMode = "signature" | "initials" | "parent"

interface Props {
    mode: SignatureMode
    /**
     * If popup opened from PDF form
     */
    signature_key: "student_signature_covid19" | string
    gate: Gate
    model: Model
    onClose: () => void
}

/**
 * Returns contextual titles and descriptions for the popup based on the active mode.
 */
function getSignatureLabels(mode: SignatureMode) {
    const isInitials = mode === "initials"
    const term = isInitials ? "initials" : "signature"

    const title =
        mode === "signature"
            ? "Manage your signature"
            : isInitials
            ? "Manage your initials"
            : "Parent Signature"

    return {
        isInitials,
        term,
        title,
        hint: `Please sign or type inside the box below. Click Clear to retry and Save to apply your ${term}.`,
        typeLabel: `or type your ${term}:`,
        placeholder: `Type ${term} here...`,
        emptyText: isInitials
            ? "You do not currently have initials saved"
            : "You do not currently have a signature saved",
        startText: `Enter your ${term} here. Click to start.`
    }
}

/**
 * Patches signature_pad internal stroke handlers to protect against undefined length errors
 * when _data is empty or desynchronized across touch/mouse event sequences.
 */
function applySignaturePadSafetyGuards(target: any) {
    if (!target || target._isLengthSafetyGuarded) return
    target._isLengthSafetyGuarded = true

    const originalStrokeUpdate = target._strokeUpdate
    if (typeof originalStrokeUpdate === "function") {
        target._strokeUpdate = function (event: any) {
            if (!this._data || !Array.isArray(this._data) || this._data.length === 0) {
                this._data = [[]]
            } else if (!this._data[this._data.length - 1]) {
                this._data[this._data.length - 1] = []
            }
            try {
                return originalStrokeUpdate.call(this, event)
            } catch (err) {
                // Prevent unhandled exception
            }
        }
    }

    const originalStrokeEnd = target._strokeEnd
    if (typeof originalStrokeEnd === "function") {
        target._strokeEnd = function (event: any) {
            if (!this._data || !Array.isArray(this._data) || this._data.length === 0) {
                this._data = [[]]
            } else if (!this._data[this._data.length - 1]) {
                this._data[this._data.length - 1] = []
            }
            const lastGroup = this._data[this._data.length - 1]
            const point = this.points && this.points.length > 0 ? this.points[0] : null
            if (point && lastGroup && lastGroup.length === 0) {
                lastGroup.push({
                    x: point.x,
                    y: point.y,
                    time: point.time,
                    color: this.penColor
                })
            }
            try {
                return originalStrokeEnd.call(this, event)
            } catch (err) {
                try {
                    if (point && lastGroup) {
                        const lastPoint = lastGroup[lastGroup.length - 1]
                        if (!lastPoint || !point.equals || !point.equals(lastPoint)) {
                            lastGroup.push({
                                x: point.x,
                                y: point.y,
                                time: point.time,
                                color: this.penColor
                            })
                        }
                    }
                    if (typeof this.onEnd === "function") {
                        this.onEnd(event)
                    }
                } catch (e) {
                    // Safe fallback
                }
            }
        }
    }
}

// Module-level guard for SignaturePad prototype
try {
    const proto = (SignaturePad as any)?.default?.prototype || (SignaturePad as any)?.prototype
    if (proto) {
        applySignaturePadSafetyGuards(proto)
    }
} catch (e) {
    // Ignore prototype access errors in non-browser test environments
}

@observer
export class SignaturePopup extends Component<Props, {}> {
    @observable
    private currentSignatureImgData: string | undefined

    @observable
    private screenCanvas: boolean = false

    @observable
    private formatSignature: "draw" | "text" = "draw"

    @observable
    private textValue: string = ""

    private sigCanvas: any
    private _isMounted: boolean = false

    constructor(props: Props) {
        super(props)
        this.hydrateFromPeekCache(props.mode)
    }

    private hydrateFromPeekCache(mode: SignatureMode) {
        if (this.props.model && typeof this.props.model.peekSignature === "function") {
            const cached: any = this.props.model.peekSignature(mode)
            if (cached && cached.json_data && cached.json_data.img) {
                this.currentSignatureImgData = cached.json_data.img
            } else {
                this.currentSignatureImgData = undefined
            }
        }
    }

    @computed
    private get imgData(): string {
        return this.currentSignatureImgData || BLANK_SIGNATURE_DATA_URL
    }

    private get signaturePadPreview(): JSX.Element {
        const labels = getSignatureLabels(this.props.mode)
        if (this.imgData === BLANK_SIGNATURE_DATA_URL) {
            return (
                <div className={styles.previewContent}>
                    <div className={styles.signatureText}>{labels.emptyText}</div>
                    <div className={styles.previewUnderline} aria-hidden="true" />
                    <div className={styles.verifiedFooter} aria-hidden="true">
                        <span className={styles.verifiedDot} />
                        <span>Verified Certificate</span>
                    </div>
                </div>
            )
        }
        return (
            <div className={styles.previewContent}>
                <img alt="" src={this.imgData} className={styles.signaturePad} />
                <div className={styles.previewUnderline} aria-hidden="true" />
                <div className={styles.verifiedFooter} aria-hidden="true">
                    <span className={styles.verifiedDot} />
                    <span>Verified Certificate</span>
                </div>
            </div>
        )
    }

    public componentDidMount() {
        this._isMounted = true
        this.toggleModalContainer(true)
        this.hydrateFromPeekCache(this.props.mode)
        this.loadCurrentSignature()
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.mode !== this.props.mode) {
            this.hydrateFromPeekCache(this.props.mode)
            this.loadCurrentSignature()
        }
    }

    public componentWillUnmount() {
        this._isMounted = false
        this.toggleModalContainer(false)
    }

    private toggleModalContainer(isOpen: boolean) {
        const modalEl = document.getElementById("appModal")
        if (modalEl) {
            modalEl.style.display = isOpen ? "block" : "none"
        }
    }

    public render() {
        const {mode, onClose} = this.props
        const labels = getSignatureLabels(mode)
        const hasContent = this.screenCanvas || Boolean(this.textValue.trim())

        const content = (
            <article className={styles.popup}>
                <div className={styles.brandEdgeStrip} aria-hidden="true" />
                <div className={styles.body}>
                    <div className={styles.header}>
                        <div className={styles.headerInfo}>
                            <div className={styles.iconWrapper} aria-hidden="true">
                                <svg
                                    className={styles.icon}
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="24"
                                    height="24">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM5.92 19H5v-.92l9.06-9.06.92.92L5.92 19zM20.71 5.63l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83a.996.996 0 0 0 0-1.41z" />
                                </svg>
                            </div>
                            <div className={styles.headerText}>
                                <div className={styles.titleRow}>
                                    <h2 className={styles.title}>{labels.title}</h2>
                                    <span className={styles.securityBadge}>
                                        <svg
                                            className={styles.securityBadgeIcon}
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            width="14"
                                            height="14"
                                            aria-hidden="true">
                                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm7 10c0 4.52-2.98 8.69-7 9.93-4.02-1.24-7-5.41-7-9.93V6.3l7-3.11 7 3.11V11zm-11.59.59L6 13l4 4 8-8-1.41-1.42L10 14.17l-2.59-2.58z" />
                                        </svg>
                                        <span>Legally Enforceable Digital Signature</span>
                                    </span>
                                </div>
                                <p className={styles.hint}>
                                    Please sign or type inside the box below. Click{" "}
                                    <strong className={styles.hintStrongClear} /> to retry and{" "}
                                    <strong className={styles.hintStrongSave}>
                                        {labels.isInitials ? "Save Initials" : "Save Signature"}
                                    </strong>{" "}
                                    to apply to your Bellus enrollment forms.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            aria-label="close.svg"
                            className={styles.closeBtn}
                            onClick={onClose}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                width="20"
                                height="20"
                                aria-hidden="true">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                            </svg>
                        </button>
                    </div>

                    <div className={styles.studioContent}>
                        <div className={styles.signatureWrapper}>
                            <div className={styles.signatureColumn}>
                                <div className={styles.boxHeader}>
                                    <span className={styles.boxLabel}>
                                        <svg
                                            className={styles.boxHeaderIconTeal}
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            width="15"
                                            height="15"
                                            aria-hidden="true">
                                            <path d="M4.59 6.89c.7-.71 1.4-1.35 1.71-1.22.5.2 0 1.03-.3 1.52-.25.42-2.86 3.89-2.86 6.31 0 1.28.48 2.34 1.34 2.98.75.56 1.74.73 2.64.46 1.07-.31 1.95-1.4 3.06-2.77 1.21-1.49 2.83-3.44 4.08-3.44 1.63 0 1.65 1.01 1.76 1.79-3.78.64-5.38 3.67-5.38 5.37 0 1.7 1.44 3.09 3.21 3.09 1.63 0 4.29-1.33 4.69-6.1H21v-2.5h-2.47c-.15-1.65-1.09-4.2-4.03-4.2-2.25 0-4.18 1.91-4.94 2.84-.58.73-2.06 2.48-2.29 2.72-.25.3-.68.84-1.11.84-.45 0-.72-.83-.36-1.92.35-1.09 1.4-2.86 1.85-3.52.78-1.14 1.3-1.92 1.3-3.28C8.95 3.69 7.31 3 6.44 3 5.12 3 3.97 4 3.72 4.25c-.36.36-.66.66-.88.93l1.75 1.71zm9.29 11.66c-.31 0-.74-.26-.74-.72 0-.6 1.31-2.2 2.87-2.76-.3 2.69-1.43 3.48-2.13 3.48z" />
                                        </svg>
                                        <span>
                                            {labels.isInitials
                                                ? "Draw your initials"
                                                : "Draw your signature"}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.clearPadBtn}
                                        onClick={this.onClear}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            width="13"
                                            height="13"
                                            aria-hidden="true">
                                            <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                                        </svg>
                                        <span>Clear pad</span>
                                    </button>
                                </div>
                                <div className={`${styles.signatureBox} ${styles.drawBox}`}>
                                    {this.renderCanvasOrPlaceholder()}
                                </div>
                            </div>
                            <div className={styles.signatureColumn}>
                                <div className={styles.boxHeader}>
                                    <span className={styles.boxLabel}>
                                        <svg
                                            className={styles.boxHeaderIconAmber}
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            width="15"
                                            height="15"
                                            aria-hidden="true">
                                            <path d="M7.5 5.6L10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a.996.996 0 0 0-1.41 0L1.29 18.96a.996.996 0 0 0 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05a.996.996 0 0 0 0-1.41l-2.33-2.35zm-1.03 5.49l-2.12-2.12 2.44-2.44 2.12 2.12-2.44 2.44z" />
                                        </svg>
                                        <span>Live Generated Preview</span>
                                    </span>
                                    <span className={styles.previewBadge}>Preview Mode</span>
                                </div>
                                <div className={`${styles.signatureBox} ${styles.previewBox}`}>
                                    {this.signaturePadPreview}
                                </div>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel} htmlFor="signature_text_input">
                                {labels.typeLabel}
                            </label>
                            <div className={styles.inputFieldWrapper}>
                                <span className={styles.inputIcon} aria-hidden="true">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        width="18"
                                        height="18">
                                        <path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z" />
                                    </svg>
                                </span>
                                <input
                                    id="signature_text_input"
                                    type="text"
                                    className={styles.textInput}
                                    placeholder={labels.placeholder}
                                    value={this.textValue}
                                    onChange={this.handleTextInput}
                                    onFocus={this.handleInputFocus}
                                    autoComplete="off"
                                />
                                <button
                                    type="button"
                                    className={styles.clearTextBtn}
                                    onClick={this.onClear}>
                                    Clear text
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttonWrapper}>
                        <div className={styles.footerLeft}>
                            <button
                                type="button"
                                className={styles.clearBtn}
                                onClick={this.onClear}
                                disabled={!hasContent}>
                                <svg
                                    className={styles.footerBtnIcon}
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="16"
                                    height="16"
                                    aria-hidden="true">
                                    <path d="M12 5V2L8 6l4 4V7c3.31 0 6 2.69 6 6 0 2.97-2.17 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93 0-4.42-3.58-8-8-8zm-6 8c0-1.65.67-3.15 1.76-4.24L6.34 7.34C4.9 8.79 4 10.79 4 13c0 4.08 3.05 7.44 7 7.93v-2.02c-2.83-.48-5-2.94-5-5.91z" />
                                </svg>
                                <span>Clear</span>
                            </button>
                            <button type="button" className={styles.cancelBtn} onClick={onClose}>
                                Cancel
                            </button>
                        </div>
                        <div className={styles.footerRight}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                onClick={this.onSave}
                                disabled={!hasContent}>
                                <svg
                                    className={styles.saveBtnIcon}
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="16"
                                    height="16"
                                    aria-hidden="true">
                                    <path d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z" />
                                </svg>
                                <span
                                    className={
                                        labels.isInitials
                                            ? styles.saveBtnLabelInitials
                                            : styles.saveBtnLabelSignature
                                    }>
                                    Save
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </article>
        )

        const modalContainer = document.getElementById("appModal")
        return modalContainer ? ReactDOM.createPortal(content, modalContainer) : content
    }

    @action
    private handleInputFocus = () => {
        if (!this.screenCanvas) {
            this.screenCanvas = true
        }
    }

    @action
    private handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.textValue = value
        this.formatSignature = "text"
        if (!this.screenCanvas) {
            this.screenCanvas = true
            setTimeout(() => {
                this.renderTextToCanvas(value)
            }, 30)
        } else {
            this.renderTextToCanvas(value)
        }
    }

    private renderTextToCanvas(value: string) {
        if (!this.sigCanvas) return
        const canvas =
            typeof this.sigCanvas.getCanvas === "function" ? this.sigCanvas.getCanvas() : null
        if (!canvas) return
        const ctx = canvas.getContext("2d")
        if (!ctx) return

        this.sigCanvas.clear()
        if (!value || !value.trim()) {
            return
        }

        const isInitials = this.props.mode === "initials"
        let fontSize = isInitials ? CANVAS_CONFIG.initialsFontSize : CANVAS_CONFIG.signatureFontSize
        ctx.font = `${fontSize}px ${CANVAS_CONFIG.fontFamily}`
        const maxWidth = canvas.width - CANVAS_CONFIG.paddingX
        const maxHeight = canvas.height - 24

        while (
            (ctx.measureText(value).width > maxWidth || fontSize > maxHeight) &&
            fontSize > CANVAS_CONFIG.minFontSize
        ) {
            fontSize -= 2
            ctx.font = `${fontSize}px ${CANVAS_CONFIG.fontFamily}`
        }

        ctx.fillStyle = "#000000"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(value, canvas.width / 2, canvas.height / 2)

        const sigPad = this.getSigPadInstance()
        if (sigPad) {
            sigPad._isEmpty = false
        }
    }

    private onCanvasBegin = () => {
        if (this.formatSignature === "text") {
            const canvas =
                typeof this.sigCanvas?.getCanvas === "function" ? this.sigCanvas.getCanvas() : null
            const ctx = canvas ? canvas.getContext("2d") : null
            if (ctx && canvas) {
                ctx.clearRect(0, 0, canvas.width, canvas.height)
            }
            this.textValue = ""
        }
        this.formatSignature = "draw"

        const sigPad = this.getSigPadInstance()
        if (sigPad) {
            if (!sigPad._data || !Array.isArray(sigPad._data)) {
                sigPad._data = [[]]
            } else if (sigPad._data.length === 0) {
                sigPad._data.push([])
            }
        }
    }

    private handleCanvasRef = (ref: any) => {
        this.sigCanvas = ref
        if (!ref) return
        const sigPad = ref.getSignaturePad ? ref.getSignaturePad() : ref._sigPad
        if (sigPad) {
            applySignaturePadSafetyGuards(sigPad)
            if (sigPad._handleMouseUp && !sigPad._mouseUpWrapped) {
                sigPad._mouseUpWrapped = true
                const origHandleMouseUp = sigPad._handleMouseUp.bind(sigPad)
                sigPad._handleMouseUp = (event: any) => {
                    try {
                        origHandleMouseUp(event)
                    } catch (err) {
                        sigPad._mouseButtonDown = false
                    }
                }
            }
        }
    }

    private getSigPadInstance() {
        return this.sigCanvas?.getSignaturePad
            ? this.sigCanvas.getSignaturePad()
            : this.sigCanvas?._sigPad
    }

    private resetSigPadState() {
        const sigPad = this.getSigPadInstance()
        if (sigPad) {
            sigPad._mouseButtonDown = false
            sigPad.points = []
            sigPad._data = []
        }
        if (this.sigCanvas) {
            this.sigCanvas.clear()
        }
    }

    private onSave = async () => {
        if (this.props.model?.document?.json_data?.isSubmitted) {
            delete this.props.model.document.json_data.isSubmitted
        }

        const isText = this.formatSignature === "text"
        const isEmpty = isText
            ? !this.textValue.trim()
            : !this.sigCanvas || this.sigCanvas.isEmpty()

        if (isEmpty) {
            const term = this.props.mode === "initials" ? "initials" : "signature"
            alert(`Can not save empty ${term}`)
            return
        }

        const imgData = this.sigCanvas.toDataURL("image/png")
        const format = this.formatSignature
        const response = await this.props.gate.request("/signatures/add", {
            data: {json_data: {img: imgData}, type: this.props.mode, format}
        })
        const savedSig = response && response.success ? response.data : null

        if (savedSig) {
            if (this.props.model?.setLocalSignature) {
                this.props.model.setLocalSignature(this.props.mode, savedSig)
            }
            this.currentSignatureImgData = savedSig.json_data ? savedSig.json_data.img : imgData
        } else {
            await this.loadCurrentSignature()
        }

        this.resetSigPadState()
        this.textValue = ""
        this.screenCanvas = false

        this.notifyChildBridge(savedSig)
        this.props.onClose()
    }

    private notifyChildBridge(savedSig: any) {
        const bridge = this.props.model?.childBridge
        if (!bridge) return

        const {mode, signature_key} = this.props
        if (mode === "signature") {
            bridge.sign(signature_key, savedSig)
        } else if (mode === "initials") {
            bridge.signInitials(signature_key, savedSig)
        } else if (mode === "parent") {
            bridge.signParent(signature_key, savedSig)
        }
    }

    @action
    private onClear = () => {
        this.resetSigPadState()
        this.textValue = ""
        this.formatSignature = "draw"
        this.screenCanvas = false
    }

    private async loadCurrentSignature() {
        const targetMode = this.props.mode
        if (this.props.model && typeof this.props.model.getSignature === "function") {
            const cachedSig: any = await this.props.model.getSignature(targetMode)
            if (!this._isMounted || this.props.mode !== targetMode) {
                return
            }
            if (cachedSig && cachedSig.json_data && cachedSig.json_data.img) {
                this.currentSignatureImgData = cachedSig.json_data.img
            }
            return
        }
        const r = await this.props.gate.request("/signatures/get-my", {
            data: {type: targetMode},
            silent: true
        })
        if (!this._isMounted || this.props.mode !== targetMode) {
            return
        }
        if (r && r.success && r.data && r.data.json_data) {
            this.currentSignatureImgData = r.data.json_data.img
            if (this.props.model && typeof this.props.model.setLocalSignature === "function") {
                this.props.model.setLocalSignature(targetMode, r.data)
            }
        }
    }

    @action
    private renderCanvasOrPlaceholder() {
        if (this.screenCanvas) {
            return (
                <SignatureCanvas
                    penColor="green"
                    canvasProps={{
                        width: CANVAS_CONFIG.width,
                        height: CANVAS_CONFIG.height,
                        className: styles.currentSignature,
                        style: {touchAction: "none"}
                    }}
                    onBegin={this.onCanvasBegin}
                    ref={this.handleCanvasRef}
                />
            )
        }

        return (
            <NoSignatureBox
                mode={this.props.mode}
                onClick={() => {
                    this.screenCanvas = true
                }}
            />
        )
    }
}

function NoSignatureBox({onClick, mode}: {onClick: () => void; mode: SignatureMode}) {
    const labels = getSignatureLabels(mode)
    return (
        <div className={styles.signatureText} onClick={onClick}>
            <svg
                className={styles.placeholderIcon}
                viewBox="0 0 24 24"
                fill="currentColor"
                width="30"
                height="30"
                aria-hidden="true">
                <path d="M3 10h11v2H3v-2zm0-2h11V6H3v2zm0 8h7v-2H3v2zm15.01-3.13l.71-.71a.996.996 0 0 1 1.41 0l.71.71c.39.39.39 1.02 0 1.41l-.71.71-2.12-2.12zm-.71.71l-5.3 5.3V21h2.12l5.3-5.3-2.12-2.12z" />
            </svg>
            <span className={styles.startTextPrimary}>{labels.startText}</span>
            <span className={styles.startTextSub}>
                Click and drag or touch with finger / stylus
            </span>
            <div className={styles.signatureBaseline} aria-hidden="true">
                <span>✕ SIGN HERE</span>
                <span>BELLUS SECURE PAD</span>
            </div>
        </div>
    )
}
